import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Box, Button, CssBaseline, Grid, Backdrop, CircularProgress } from "@mui/material";
import { query, where, getDocs, orderBy, updateDoc, doc, getDoc, serverTimestamp, collection, onSnapshot, addDoc, getCountFromServer, limit } from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from "firebase/storage";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import Sidebar from "../../../../common/Sidebar";
import { mainLayoutStyles, showNotification, routes } from "../../../../constants";
import QuickTalesTable from "../../../OrganizationQuickTales/components/QuickTalesTable/QuickTalesTable";
import { BatchQuicktalesRef, db, functions, storage, promoteBatchQuicktale } from "../../../../firebase";
import { FilterSection, SearchBar, GetLinksModal } from "../../../OrganizationQuickTales/components/components";
import { AddQuickTalesToOrgTableRow } from "../../../AddQuickTalesToOrganization/components/components";
import { AddQuicktalesToOrgTableHeadCells } from "../../../../constants";
import FilterModalComponent from "../../../../common/FilterModalComponent";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import { getDateRange } from "../../../OrgProf/hook/Organizationhook";
import { updateBatchQuickTale } from "../../hooks/BatchQuickTalesHooks";
import { Toast } from "../../../../common/alert";
import { useAuth } from "../../../../hooks";
import { useGetEmailTemplates } from "../../../OrganizationEmailTemplates/hooks/EmailTemplatesHooks";
import { getCustomNarratorByName, getCustomNarratorByNameUnscoped, updateCustomNarrator } from "../../../OrganizationQuickTales/hooks/CustomNarratorsHooks";
import BulkAreaModal from "../../components/BulkAreaModal/BulkAreaModal";
// NOTE: Sorting/pagination state was moved out of QuickTalesProvider.
// BatchTableContainer manages its own sort state now.

const BatchTableContainer = () => {
  const { batchId: paramBatchId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // NOTE: The sorting state is managed inside <QuickTalesTable />.
  // BatchTableContainer previously used provider sort state; keep the defaults
  // via props instead (see QuickTalesTable usage below).

  // Start with name only if explicitly provided via navigation state; otherwise
  // leave empty so Firestore-loaded batch details can set the correct name.
  const [batchName, setBatchName] = useState(location?.state?.batch?.batchName || "");
  const [batchDetails, setBatchDetails] = useState(location?.state?.batch || null);
  const [quicktales, setQuicktales] = useState([]);
  const [allQuicktales, setAllQuicktales] = useState([]); // Store all for filtering
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  // Email templates & narrator details state for Send Links
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [emailPromptModalOpen, setEmailPromptModalOpen] = useState(false);
  const [narratorEmail, setNarratorEmail] = useState("");
  const [narratorFirstName, setNarratorFirstName] = useState("");
  const [narratorLastName, setNarratorLastName] = useState("");
  const [narratorsWithoutEmails, setNarratorsWithoutEmails] = useState([]);
  const [currentNarratorIndex, setCurrentNarratorIndex] = useState(0);

  // Missing Narrator Check States
  const [missingNarratorQueue, setMissingNarratorQueue] = useState([]);
  const [validQuickTalesForSend, setValidQuickTalesForSend] = useState([]);
  const [pendingTemplateId, setPendingTemplateId] = useState("");
  const [showMissingNarratorModal, setShowMissingNarratorModal] = useState(false);

  const organizationIdForTemplates =
    batchDetails?.organization || location?.state?.batch?.organization || currentUser?.organization;

  // When we touch a customNarrator record in batch flows, persist its org for future shared lookups.
  const customNarratorOrganizationId = organizationIdForTemplates || "";

  const { templates: allEmailTemplates } = useGetEmailTemplates(organizationIdForTemplates);
  const activeEmailTemplates = allEmailTemplates?.filter((t) => t.status === "active") || [];
  
  // Filter and search states
  const [filterModal, setFilterModal] = useState(false);
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);
  const [whoCanListenFilterOptions, setWhoCanListenFilterOptions] = useState([]);
  const [modifiedStatusDateFilterOptions, setModifiedStatusDateFilterOptions] = useState([]);
  const [getLinkModalOpen, setGetLinkModalOpen] = useState(false);
  const [generatedLinks, setGeneratedLinks] = useState([]);

  // Get Link template selection states
  const [getLinkTemplateSelectionModalOpen, setGetLinkTemplateSelectionModalOpen] = useState(false);
  const [selectedGetLinkTemplate, setSelectedGetLinkTemplate] = useState("");

  const batchId = paramBatchId || location?.state?.batch?.id;

  // Areas (for bulk Area modal)
  const [areas, setAreas] = useState([]);
  const [bulkAreaModalOpen, setBulkAreaModalOpen] = useState(false);
  const [bulkAreaLoading, setBulkAreaLoading] = useState(false);

  // Map for fast lookup of area name by id for table rendering.
  const areasById = useMemo(() => {
    const map = new Map();
    (areas || []).forEach((a) => {
      if (a?.id) map.set(a.id, a);
    });
    return map;
  }, [areas]);

  const normalizeAreaName = (value) => {
    try {
      return String(value || "")
        .trim()
        .replace(/\s+/g, " ");
    } catch {
      return "";
    }
  };

  const reservedAreaNames = new Set(["all", "nearby"]);

  const ensureAreaExists = async ({ orgId, areaNameRaw, userId }) => {
    const areaName = normalizeAreaName(areaNameRaw);
    if (!areaName) return null;
    if (reservedAreaNames.has(areaName.toLowerCase())) {
      throw new Error('"All" and "Nearby" are reserved Area names.');
    }

    const existingQ = query(
      collection(db, "area"),
      where("organization", "==", orgId),
      where("name", "==", areaName)
    );
    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) {
      return { id: existingSnap.docs[0].id, name: areaName };
    }

    let nextPosition = 1;
    try {
      const countQ = query(collection(db, "area"), where("organization", "==", orgId));
      const countSnap = await getCountFromServer(countQ);
      const docCount = countSnap?.data()?.count;
      if (typeof docCount === "number" && Number.isFinite(docCount)) {
        nextPosition = docCount + 3;
      }
    } catch (e) {
      console.warn("ensureAreaExists (bulk area): count failed, defaulting position=1", e);
    }

    const ref = await addDoc(collection(db, "area"), {
      name: areaName,
      organization: orgId,
      isActive: true,
      isDeleted: false,
      position: nextPosition,
      albumsAndQTs: [],
      createdAt: serverTimestamp(),
      createdBy: userId || "",
      updatedAt: serverTimestamp(),
      updatedBy: userId || "",
    });

    return { id: ref.id, name: areaName };
  };

  // Auto-select default or single active template when template modal opens
  useEffect(() => {
    if (!templateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedTemplate) return; // Don't override user choice

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }
  }, [templateSelectionModalOpen, activeEmailTemplates, selectedTemplate]);

  // Auto-select default or single active template when Get Link template modal opens
  useEffect(() => {
    if (!getLinkTemplateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedGetLinkTemplate) return;

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedGetLinkTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }
  }, [
    getLinkTemplateSelectionModalOpen,
    activeEmailTemplates,
    selectedGetLinkTemplate,
  ]);

  // Search function
  const handleSearchQuickTales = (e) => {
    try {
      const value = e.target.value;
      if (value.length >= 1) {
        const searchTerm = value.toLowerCase();
        const filtered = allQuicktales.filter((qt) => {
          return (
            (qt.title && qt.title.toLowerCase().includes(searchTerm)) ||
            (qt.createdByData?.userName && qt.createdByData.userName.toLowerCase().includes(searchTerm)) ||
            (qt.narratorData?.userName && qt.narratorData.userName.toLowerCase().includes(searchTerm)) ||
            (qt.narrator && qt.narrator.toLowerCase().includes(searchTerm))
          );
        });
        setQuicktales(filtered);
      } else {
        setQuicktales(allQuicktales);
      }
    } catch (error) {
      console.log("error searching quicktales:", error.message);
    }
  };

  // Filter function
  const handleFilterQuickTales = () => {
    setSelected([]);
    let filtered = [...allQuicktales];

    // Apply Who Can Listen filter
    if (whoCanListenFilterOptions.length > 0) {
      filtered = filtered.filter((qt) =>
        whoCanListenFilterOptions.includes(qt.status)
      );
    }

    // Apply Status filter (approvalStatus)
    if (statusFilterOptions.length > 0) {
      filtered = filtered.filter((qt) =>
        statusFilterOptions.includes(qt.approvalStatus?.status)
      );
    }

    // Apply Modified Status Date filter
    if (modifiedStatusDateFilterOptions.length > 0) {
      const dateRange = getDateRange(modifiedStatusDateFilterOptions, "Modified Status Date");
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        filtered = filtered.filter((qt) => {
          if (!qt.approvalStatus?.updatedAt) return false;
          const qtDate = qt.approvalStatus.updatedAt.toDate ? qt.approvalStatus.updatedAt.toDate() : qt.approvalStatus.updatedAt;
          const qtTime = new Date(qtDate).getTime();
          return qtTime >= dateRange.startDate.getTime() && qtTime <= dateRange.endDate.getTime();
        });
      }
    }

    setQuicktales(filtered);
    setFilterModal(false);
  };

  // Handle filter chip deletion
  const handleDeleteChip = (filterType, filterItem) => {
    if (filterType === "status") {
      setStatusFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    } else if (filterType === "whoCanListen") {
      setWhoCanListenFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    } else if (filterType === "modifiedStatusDate") {
      setModifiedStatusDateFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    }
  };

  // Reset filters
  const resetFilter = () => {
    setStatusFilterOptions([]);
    setWhoCanListenFilterOptions([]);
    setModifiedStatusDateFilterOptions([]);
    setQuicktales(allQuicktales);
    setSelected([]);
    setFilterModal(false);
  };

  // Bulk action: Update QuickTale Status (approval status)
  const handleUpdateQuickTaleStatus = async (status) => {
    try {
      setLoading(true);
      const dataTemp = {
        approvalStatus: {
          status: status,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.id,
        },
        updatedAt: serverTimestamp(),
      };
      await Promise.all(selected.map((id) => updateBatchQuickTale(id, dataTemp)));

      // If status changed to approved, promote each selected Batch QuickTale
      if (status === "approved") {
        await Promise.all(
          selected.map((id) => promoteBatchQuicktale({ batchQuicktaleId: id }))
        );
      }

      setSelected([]);
      showNotification("QuickTale(s) added to QuickTales in Organization.");
      // Refresh data
      const data = await fetchQuicktalesData();
      setAllQuicktales(data);
      setQuicktales(data);
    } catch (error) {
      showNotification("Error adding QuickTale(s) to QuickTales in Organization.  Please contact support.");
      console.log("Error promoting quicktale", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Bulk action: Update Who Can Listen (status field)
  const handleUpdateQuickTaleApprovalStatus = async (status) => {
    try {
      setLoading(true);
      const dataTemp = {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.id,
      };
      await Promise.all(selected.map((id) => updateBatchQuickTale(id, dataTemp)));
      setSelected([]);
      showNotification("QuickTale(s) updated successfully.");
      // Refresh data
      const data = await fetchQuicktalesData();
      setAllQuicktales(data);
      setQuicktales(data);
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating quicktale", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkArea = () => {
    setBulkAreaModalOpen(true);
  };

  // Bulk action: Set autoApprove field on all selected rows
  const handleBulkAutoApprove = async (value) => {
    try {
      setLoading(true);
      const dataTemp = {
        autoApprove: value,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.id,
      };
      await Promise.all(selected.map((id) => updateBatchQuickTale(id, dataTemp)));
      setSelected([]);
      showNotification(`Auto Approve set to ${value ? "On" : "Off"} for selected QuickTale(s).`);
      const data = await fetchQuicktalesData();
      setAllQuicktales(data);
      setQuicktales(data);
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating autoApprove", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAreaUpdate = async (areaName) => {
    const orgId = organizationIdForTemplates;
    const userId = currentUser?.id;
    const normalized = normalizeAreaName(areaName);

    if (!normalized) return;
    if (reservedAreaNames.has(normalized.toLowerCase())) {
      showNotification('"All" and "Nearby" are reserved Area names.');
      return;
    }

    try {
      setBulkAreaLoading(true);

      const area = await ensureAreaExists({
        orgId,
        areaNameRaw: normalized,
        userId,
      });

      // Safety: do not update approved rows even if somehow selected
      const selectedQuickTales = allQuicktales.filter((qt) => selected.includes(qt.id));
      const eligibleIds = selectedQuickTales
        .filter((qt) => qt?.approvalStatus?.status !== "approved")
        .map((qt) => qt.id);

      if (eligibleIds.length === 0) {
        showNotification("No eligible QuickTales selected.");
        return;
      }

      const dataTemp = {
        areaIdResolved: area?.id || "",
        areaNameFromCsv: area?.name || normalized,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      };

      await Promise.all(eligibleIds.map((id) => updateBatchQuickTale(id, dataTemp)));

      setSelected([]);
      setBulkAreaModalOpen(false);
      showNotification("QuickTale(s) updated successfully.");

      const data = await fetchQuicktalesData();
      setAllQuicktales(data);
      setQuicktales(data);
    } catch (e) {
      console.error("Bulk area update failed:", e);
      Toast("error", e?.message || "Error updating Area");
    } finally {
      setBulkAreaLoading(false);
    }
  };

  // Bulk action: Get links
  const handleGetLink = async () => {
    if (!selected.length) {
      showNotification("Please select at least one QuickTale.");
      return;
    }

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification("Please create an email template");
      return;
    }

    if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }

    setGetLinkTemplateSelectionModalOpen(true);
  };

  const handleGetLinkTemplateSelectionContinue = async () => {
    if (!selectedGetLinkTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setGetLinkTemplateSelectionModalOpen(false);
    setLoading(true);

    try {
      console.groupCollapsed('[BatchTableContainer] Get Link');
      console.log('selectedGetLinkTemplate:', selectedGetLinkTemplate);
      console.log('selected quicktale ids:', selected);
      console.log('customNarratorOrganizationId:', customNarratorOrganizationId);
      console.log('currentUser ids:', {
        id: currentUser?.id,
        uid: currentUser?.uid,
        userId: currentUser?.userId,
        organization: currentUser?.organization,
      });

      const generateExternalUpdateLink = httpsCallable(functions, 'generateExternalUpdateLink');
      const links = await Promise.all(selected.map(async (quicktaleId) => {
        console.groupCollapsed('[BatchTableContainer] Get Link -> quicktale', quicktaleId);
        const quicktale = allQuicktales.find(qt => qt.id === quicktaleId);
        // Resolve narrator info for activity logging
        const narratorNameForLog = quicktale?.narratorData
          ? `${quicktale.narratorData.firstName || ''} ${quicktale.narratorData.lastName || ''}`.trim()
          : (quicktale?.narrator || '');
        const narratorEmailForLog = quicktale?.narratorData?.email || '';

        const result = await generateExternalUpdateLink({
          quicktaleId: quicktaleId,
          origin: window.location.origin,
          collection: 'batchQuicktales',
          emailTemplateId: selectedGetLinkTemplate,
          narratorName: narratorNameForLog,
          narratorEmail: narratorEmailForLog,
        });
        console.log('generateExternalUpdateLink result:', result?.data);
        console.log('quicktale found in allQuicktales:', {
          id: quicktale?.id,
          narrator: quicktale?.narrator,
          hasNarratorData: !!quicktale?.narratorData,
          narratorDataEmail: quicktale?.narratorData?.email,
          organization: quicktale?.organization,
          createdBy: quicktale?.createdBy,
        });

        // Best effort: if this is a custom narrator and we can resolve the doc,
        // persist organization so the narrator page can resolve tokens by org.
        try {
          const narratorName = (quicktale?.narrator || '').trim();
          console.log('narratorName:', narratorName);
          console.log('isCustomNarratorCandidate:', !!narratorName && !quicktale?.narratorData);
          console.log('customNarratorOrganizationId present:', !!customNarratorOrganizationId);

          if (!narratorName) {
            console.log('Skip org upsert: no narratorName');
          } else if (quicktale?.narratorData) {
            console.log('Skip org upsert: narratorData present (registered user)');
          } else if (!customNarratorOrganizationId) {
            console.log('Skip org upsert: missing customNarratorOrganizationId');
          } else {
            const userIdToUse = currentUser?.userId || currentUser?.uid;
            console.log('Attempt getCustomNarratorByName:', { narratorName, userIdToUse });
            const cn = await getCustomNarratorByName(narratorName, userIdToUse);
            console.log('getCustomNarratorByName result:', cn);

            if (!cn?.id) {
              console.log('Not found with user scope; attempting unscoped narrator lookup by name only...');
              const cnUnscoped = await getCustomNarratorByNameUnscoped(narratorName);
              console.log('getCustomNarratorByNameUnscoped result:', cnUnscoped);

              if (!cnUnscoped?.id) {
                console.log('Org upsert not possible: custom narrator doc not found even unscoped');
              } else if (cnUnscoped?.organization) {
                console.log('Org upsert not needed: unscoped narrator already has organization', cnUnscoped.organization);
              } else {
                console.log('Updating unscoped custom narrator with organization...', {
                  docId: cnUnscoped.id,
                  organization: customNarratorOrganizationId,
                });
                await updateCustomNarrator(cnUnscoped.id, { organization: customNarratorOrganizationId });
                console.log('Unscoped update succeeded (no error thrown)');
              }
            } else if (cn?.organization) {
              console.log('Org upsert not needed: custom narrator already has organization', cn.organization);
            } else {
              console.log('Updating custom narrator with organization...', {
                docId: cn.id,
                organization: customNarratorOrganizationId,
              });
              await updateCustomNarrator(cn.id, { organization: customNarratorOrganizationId });
              console.log('Update succeeded (no error thrown)');
            }
          }
        } catch (e) {
          // Don't block link generation.
          console.warn('Get Link: unable to upsert customNarrator.organization', e);
        }

        console.groupEnd();

        return {
          link: result.data.link,
          title: quicktale?.title || "Untitled",
        };
      }));

      if (links.length > 0) {
        setGeneratedLinks(links);
        setGetLinkModalOpen(true);
      } else {
        showNotification("No links could be generated");
      }
    } catch (error) {
      Toast("error", `Error generating links: ${error.message}`);
      console.error("Error calling generateExternalUpdateLink:", error);
    } finally {
      console.groupEnd();
      setLoading(false);
      setSelectedGetLinkTemplate("");
    }
  };

  const handleGetLinkTemplateSelectionClose = () => {
    setGetLinkTemplateSelectionModalOpen(false);
    setSelectedGetLinkTemplate("");
  };

  // Bulk action: Send links with email templates
  const handleSendLink = () => {
    if (!selected.length) {
      showNotification("Please select at least one QuickTale.");
      return;
    }

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification("Please create at least one active email template before sending links.");
      return;
    }

    if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }

    setTemplateSelectionModalOpen(true);
  };

  const handleTemplateSelectionContinue = async () => {
    if (!selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setTemplateSelectionModalOpen(false);
    
    // Start Missing Narrator Check
    const selectedQuickTales = allQuicktales.filter((qt) => selected.includes(qt.id));
    const missingNarratorQTs = selectedQuickTales.filter(qt => !qt.narrator || qt.narrator.trim() === "");
    const validQTs = selectedQuickTales.filter(qt => qt.narrator && qt.narrator.trim() !== "");

    if (missingNarratorQTs.length > 0) {
      setMissingNarratorQueue(missingNarratorQTs);
      setValidQuickTalesForSend(validQTs);
      setPendingTemplateId(selectedTemplate);
      setShowMissingNarratorModal(true);
    } else {
      await processSendLinksWithTemplate(selectedTemplate);
    }
  };

  const handleMissingNarratorContinue = async () => {
    // Skip the current QuickTale (first in queue)
    const remainingQueue = missingNarratorQueue.slice(1);
    
    if (remainingQueue.length > 0) {
      setMissingNarratorQueue(remainingQueue);
    } else {
      setShowMissingNarratorModal(false);
      // Proceed with the valid QuickTales we collected earlier
      await processSendLinksWithTemplate(pendingTemplateId, validQuickTalesForSend);
      
      // Reset states
      setMissingNarratorQueue([]);
      setValidQuickTalesForSend([]);
      setPendingTemplateId("");
    }
  };

  const handleMissingNarratorCancel = () => {
    setShowMissingNarratorModal(false);
    setMissingNarratorQueue([]);
    setValidQuickTalesForSend([]);
    setPendingTemplateId("");
    setSelectedTemplate("");
  };

  const handleTemplateSelectionClose = () => {
    setTemplateSelectionModalOpen(false);
    setSelectedTemplate("");
  };

  const processSendLinksWithTemplate = async (templateId, customQuickTales = null) => {
    setLoading(true);
    const narratorsToEmail = [];
    const narratorsMissingEmails = [];

    // Use customQuickTales if provided (from missing narrator flow), otherwise filter from selected IDs
    const selectedQuickTales = customQuickTales || allQuicktales.filter((qt) => selected.includes(qt.id));
    
    const groupedByNarrator = selectedQuickTales.reduce((acc, qt) => {
      const narratorKey = qt.narrator;
      if (!narratorKey) return acc;
      if (!acc[narratorKey]) {
        acc[narratorKey] = [];
      }
      acc[narratorKey].push(qt);
      return acc;
    }, {});

    for (const narratorName in groupedByNarrator) {
      const quicktalesForNarrator = groupedByNarrator[narratorName];

      // Check if any quicktale already has narratorData with an email
      const qtWithNarratorData = quicktalesForNarrator.find(
        (qt) => qt.narratorData && qt.narratorData.email
      );

      if (qtWithNarratorData) {
        const userData = qtWithNarratorData.narratorData;
        narratorsToEmail.push({
          narratorEmailAddress: userData.email,
          quicktales: quicktalesForNarrator,
          firstName: userData.firstName,
          lastName: userData.lastName,
        });
      } else {
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        let customNarrator = await getCustomNarratorByName(narratorName, userIdToUse);
        if (!customNarrator?.id) {
          // Fallback: allow other admins to resolve custom narrators not created by them.
          customNarrator = await getCustomNarratorByNameUnscoped(narratorName);
        }

        // Best effort: persist org scope whenever we can resolve a custom narrator.
        if (customNarrator?.id && customNarratorOrganizationId && !customNarrator?.organization) {
          try {
            await updateCustomNarrator(customNarrator.id, { organization: customNarratorOrganizationId });
          } catch (e) {
            // Do not block send flow.
            console.warn('Send Link: unable to upsert customNarrator.organization', e);
          }
        }

        const hasEmail = !!customNarrator?.email;
        const hasFirstName = !!customNarrator?.firstName;
        const hasLastName = !!customNarrator?.lastName;

        if (hasEmail && hasFirstName && hasLastName) {
          narratorsToEmail.push({
            narratorEmailAddress: customNarrator.email,
            quicktales: quicktalesForNarrator,
            firstName: customNarrator.firstName,
            lastName: customNarrator.lastName,
          });
        } else {
          narratorsMissingEmails.push({ customNarrator, quicktales: quicktalesForNarrator });
        }
      }
    }

    if (narratorsMissingEmails.length > 0) {
      setNarratorsWithoutEmails(narratorsMissingEmails);
      setCurrentNarratorIndex(0);
      const firstNarrator = narratorsMissingEmails[0];
      setNarratorEmail(firstNarrator.customNarrator?.email || "");
      setNarratorFirstName(firstNarrator.customNarrator?.firstName || "");
      setNarratorLastName(firstNarrator.customNarrator?.lastName || "");
      setEmailPromptModalOpen(true);
      setLoading(false); // Wait for user input before sending
    } else {
      await sendEmails(narratorsToEmail, templateId);
      setLoading(false);
    }
  };

  const sendEmails = async (narratorsToEmail, templateId) => {
    try {
      for (const { narratorEmailAddress, quicktales, firstName, lastName } of narratorsToEmail) {
        if (quicktales.length === 1) {
          const sendExternalUpdateLink = httpsCallable(functions, 'sendExternalUpdateLink');
          await sendExternalUpdateLink({
            quicktaleId: quicktales[0].id,
            email: narratorEmailAddress,
            firstName,
            lastName,
            quickTale: { title: quicktales[0].title },
            origin: window.location.origin,
            emailTemplateId: templateId,
            collection: 'batchQuicktales',
          });
        } else {
          const sendBulkExternalUpdateLink = httpsCallable(functions, 'sendBulkExternalUpdateLink');
          await sendBulkExternalUpdateLink({
            quickTales: quicktales,
            email: narratorEmailAddress,
            firstName,
            lastName,
            origin: window.location.origin,
            emailTemplateId: templateId,
            collection: 'batchQuicktales',
          });
        }
      }
      showNotification("Update link(s) sent successfully.");
    } catch (error) {
      Toast("error", `Error sending link: ${error.message}`);
      console.error("Error sending batch links:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailPromptContinue = async () => {
    // Validate all required fields
    if (!narratorFirstName) {
      showNotification("Please enter a first name.");
      return;
    }
    if (!narratorEmail) {
      showNotification("Please enter an email address.");
      return;
    }

    const currentNarrator = narratorsWithoutEmails[currentNarratorIndex];

    try {
      if (currentNarrator.customNarrator && currentNarrator.customNarrator.id) {
        await updateCustomNarrator(currentNarrator.customNarrator.id, {
          email: narratorEmail,
          firstName: narratorFirstName,
          lastName: narratorLastName,
          ...(customNarratorOrganizationId
            ? { organization: customNarratorOrganizationId }
            : {}),
        });
      } else {
        throw new Error("Custom narrator document not found.");
      }

      if (currentNarratorIndex < narratorsWithoutEmails.length - 1) {
        const nextIndex = currentNarratorIndex + 1;
        const nextNarrator = narratorsWithoutEmails[nextIndex];
        setCurrentNarratorIndex(nextIndex);
        setNarratorEmail(nextNarrator.customNarrator?.email || "");
        setNarratorFirstName(nextNarrator.customNarrator?.firstName || "");
        setNarratorLastName(nextNarrator.customNarrator?.lastName || "");
      } else {
        setEmailPromptModalOpen(false);
        setNarratorEmail("");
        setNarratorFirstName("");
        setNarratorLastName("");
        await processSendLinksWithTemplate(selectedTemplate);
      }
    } catch (error) {
      Toast("error", `Failed to save narrator details: ${error.message}`);
      console.error("Error updating custom narrator:", error);
    }
  };

  // Helper function to fetch quicktales data
  const fetchQuicktalesData = async () => {
    if (!batchId) return [];
    
    const q = query(
      BatchQuicktalesRef,
      where("batchId", "==", batchId),
      orderBy('createdAt', "desc")
    );
    const snapshot = await getDocs(q);
    
    const dataPromises = snapshot.docs.map(async (docSnapshot) => {
      const d = docSnapshot.data();
      
      let createdByData = null;
      if (d.createdBy) {
        try {
          const userDoc = await getDoc(doc(db, "users", d.createdBy));
          if (userDoc.exists()) {
            createdByData = userDoc.data();
          }
        } catch (error) {
          console.error("Error fetching createdBy user:", error);
        }
      }
      
      let narratorData = null;
      if (d.narrator) {
        try {
          const narratorDoc = await getDoc(doc(db, "users", d.narrator));
          if (narratorDoc.exists()) {
            narratorData = narratorDoc.data();
          }
        } catch (error) {
          console.error("Error fetching narrator user:", error);
        }
      }
      
      // Fetch latest email shareActivity event
      let lastEmailSentAt = null;
      let lastEmailStatus = "";
      try {
        const activityQ = query(
          collection(db, "batchQuicktales", docSnapshot.id, "shareActivity"),
          where("eventType", "==", "email"),
          orderBy("createdAt", "desc"),
          limit(1)
        );
        const activitySnap = await getDocs(activityQ);
        if (!activitySnap.empty) {
          const activityData = activitySnap.docs[0].data();
          lastEmailSentAt = activityData.createdAt || null;
          lastEmailStatus = activityData.emailStatus || "";
        }
      } catch (err) {
        console.error("Error fetching shareActivity for", docSnapshot.id, err);
      }

      return {
        id: docSnapshot.id,
        ...d,
        photoUrl: d.imageUrls?.[0] || "",
        createdByData,
        narratorData,
        lastEmailSentAt,
        lastEmailStatus,
      };
    });
    
    return await Promise.all(dataPromises);
  };

  // Apply filters when filter options change
  useEffect(() => {
    if (allQuicktales.length > 0) {
      handleFilterQuickTales();
    }
  }, [statusFilterOptions, whoCanListenFilterOptions, modifiedStatusDateFilterOptions]);

  // Custom navigation handler for batch quicktales
  const handleRowClick = (row) => {
    const path = routes.batchQuicktaleDetails
      .replace(':batchId', batchId)
      .replace(':batchQuicktaleId', row.id);
    navigate(path, {
      state: { quicktaleDetails: row },
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!batchId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const q = query(
          BatchQuicktalesRef,
          where("batchId", "==", batchId),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        
        // Fetch user data for each quicktale
        const dataPromises = snapshot.docs.map(async (docSnapshot) => {
          const d = docSnapshot.data();
          
          // Fetch createdBy user data
          let createdByData = null;
          if (d.createdBy) {
            try {
              const userDoc = await getDoc(doc(db, "users", d.createdBy));
              if (userDoc.exists()) {
                createdByData = userDoc.data();
              }
            } catch (error) {
              console.error("Error fetching createdBy user:", error);
            }
          }
          
          // Fetch narrator user data
          let narratorData = null;
          if (d.narrator) {
            try {
              const narratorDoc = await getDoc(doc(db, "users", d.narrator));
              if (narratorDoc.exists()) {
                narratorData = narratorDoc.data();
              }
            } catch (error) {
              console.error("Error fetching narrator user:", error);
            }
          }
          
          // Fetch latest email shareActivity event
          let lastEmailSentAt = null;
          let lastEmailStatus = "";
          try {
            const activityQ = query(
              collection(db, "batchQuicktales", docSnapshot.id, "shareActivity"),
              where("eventType", "==", "email"),
              orderBy("createdAt", "desc"),
              limit(1)
            );
            const activitySnap = await getDocs(activityQ);
            if (!activitySnap.empty) {
              const activityData = activitySnap.docs[0].data();
              lastEmailSentAt = activityData.createdAt || null;
              lastEmailStatus = activityData.emailStatus || "";
            }
          } catch (err) {
            console.error("Error fetching shareActivity for", docSnapshot.id, err);
          }

          return {
            id: docSnapshot.id,
            ...d,
            photoUrl: d.imageUrls?.[0] || "",
            createdByData,
            narratorData,
            lastEmailSentAt,
            lastEmailStatus,
          };
        });
        
        const data = await Promise.all(dataPromises);
        setAllQuicktales(data); // Store all quicktales
        setQuicktales(data); // Display all initially
        if (!batchName && location?.state?.batch?.batchName) {
          setBatchName(location.state.batch.batchName);
        }
      } catch (error) {
        console.error("Error loading batch quicktales:", error);
        showNotification("Error loading QuickTales for this batch");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // Subscribe to Areas for this organization (for Bulk Area modal)
  useEffect(() => {
    const orgId = organizationIdForTemplates;
    if (!orgId) {
      setAreas([]);
      return;
    }

    const q = query(
      collection(db, "area"),
      where("organization", "==", orgId),
      orderBy("position")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAreas(data);
      },
      () => setAreas([])
    );

    return unsubscribe;
  }, [organizationIdForTemplates]);

  // Load batch details (including csvFilePath) if not provided via location state
  useEffect(() => {
    const fetchBatchDetails = async () => {
      if (!batchId) return;
      try {
        const batchDoc = await getDoc(doc(db, "batches", batchId));
        if (batchDoc.exists()) {
          const data = { id: batchDoc.id, ...batchDoc.data() };
          setBatchDetails(data);
          if (!batchName && data.batchName) {
            setBatchName(data.batchName);
          }
        }
      } catch (error) {
        console.error("Error loading batch details:", error);
        showNotification("Error loading batch details");
      }
    };

    if (!batchDetails || !batchDetails.csvFilePath) {
      fetchBatchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const handleDownloadCsv = async () => {
    try {
      if (!batchDetails || !batchDetails.csvFilePath) {
        showNotification("No CSV file is associated with this batch");
        return;
      }

      setLoading(true);

      const fileRef = ref(storage, batchDetails.csvFilePath);
      const url = await getDownloadURL(fileRef);

      const link = document.createElement("a");
      link.href = url;
      link.download = batchDetails.csvOriginalFilename || `${batchDetails.batchName || "batch"}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading batch CSV:", error);
      showNotification("Error downloading CSV file");
    } finally {
      setLoading(false);
    }
  };

  const tableData = useMemo(() => quicktales, [quicktales]);

  // Enrich rows with derived areaName so the row component can render it.
  // (Table columns are driven by AddQuicktalesToOrgTableHeadCells)
  const tableDataWithArea = useMemo(() => {
    return (tableData || []).map((qt) => {
      const areaId = qt?.areaIdResolved;
      const area = areaId ? areasById.get(areaId) : null;
      return {
        ...qt,
        areaNameResolved: area?.name || "",
      };
    });
  }, [tableData, areasById]);

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`Batch: ${batchName || "Batch QuickTales"}`}
        bgColor="#fff"
        showBackButton={true}
        onBackClick={() => navigate(routes.batches)}
        additionalButton={"Download CSV"}
        additionalButtonUrl={handleDownloadCsv}
      />
      <Box sx={{ position: "absolute", top: 12, right: 200, zIndex: 3000 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(routes.guestContributors)}
          sx={{
            color: "#00bcd4",
            borderColor: "#00bcd4",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            padding: "7px 20px",
            "&:hover": {
              borderColor: "#00acc1",
              backgroundColor: "rgba(0, 188, 212, 0.04)",
            },
          }}
        >
          Guest Contributors
        </Button>
      </Box>
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <FilterSection
            handleFilterModalOpen={() => setFilterModal(true)}
            statusFilterOptions={statusFilterOptions}
            whoCanListenFilterOptions={whoCanListenFilterOptions}
            modifiedStatusDateFilterOptions={modifiedStatusDateFilterOptions}
            handleDeleteChip={handleDeleteChip}
          />
          <SearchBar
            selected={selected}
            placeholder={"Search Title, Created By, Told By"}
            handleBulkArea={handleBulkArea}
            handleBulkAutoApprove={handleBulkAutoApprove}
            handleUpdateQuickTaleStatus={handleUpdateQuickTaleStatus}
            handleUpdateQuickTaleApprovalStatus={handleUpdateQuickTaleApprovalStatus}
            handleSearch={handleSearchQuickTales}
            handleGetLink={handleGetLink}
            handleSendLink={handleSendLink}
          />
          <QuickTalesTable
            quictalesInOrg={tableDataWithArea}
            selected={selected}
            setSelected={setSelected}
            onRowClick={handleRowClick}
            tableHeadCells={AddQuicktalesToOrgTableHeadCells}
            RowComponent={AddQuickTalesToOrgTableRow}
            defaultOrder="asc"
            defaultOrderBy="approvalStatus.status"
            defaultSecondaryOrderBy="approvalStatus.updatedAt"
            isRowSelectable={(row) => row?.approvalStatus?.status !== "approved"}
            rowProps={{
              isBatchMode: true,
              onUpdateApprovalStatus: async (status, id) => {
                try {
                  setLoading(true);
                  const dataTemp = {
                    approvalStatus: {
                      status: status,
                      updatedAt: serverTimestamp(),
                      updatedBy: currentUser?.id,
                    },
                    updatedAt: serverTimestamp(),
                  };
                  await updateBatchQuickTale(id, dataTemp);

                  if (status === "approved") {
                    await promoteBatchQuicktale({ batchQuicktaleId: id });
                  }

                  showNotification("QuickTale updated successfully.");
                  const data = await fetchQuicktalesData();
                  setAllQuicktales(data);
                  setQuicktales(data);
                } catch (error) {
                  Toast("error", error.message);
                  console.log("error updating quicktale", error.message);
                } finally {
                  setLoading(false);
                }
              },
            }}
          />
        </Grid>
      </Grid>

      <FilterModalComponent
        modalOpen={filterModal}
        handleClose={() => setFilterModal(false)}
        resetFilter={resetFilter}
        handleFilter={handleFilterQuickTales}
        data={[
          {
            mainOptions: whoCanListenFilterOptions,
            setMainOptions: setWhoCanListenFilterOptions,
            type: "text",
            title: "Who Can Listen",
            currentOptions: [
              { label: "All", selected: true },
              { label: "public", selected: false },
              { label: "private", selected: false },
              { label: "unlisted", selected: false },
            ],
          },
          {
            mainOptions: statusFilterOptions,
            setMainOptions: setStatusFilterOptions,
            type: "text",
            title: "Status",
            currentOptions: [
              { label: "All", selected: true },
              { label: "approved", selected: false },
              { label: "pending", selected: false },
              { label: "denied", selected: false },
              { label: "removed", selected: false },
            ],
          },
          {
            mainOptions: modifiedStatusDateFilterOptions,
            setMainOptions: setModifiedStatusDateFilterOptions,
            type: "date",
            title: "Modified Status Date",
            currentOptions: [
              { label: "All", selected: true },
              { label: "Today", selected: false },
              { label: "Past Week", selected: false },
              { label: "Last 30 Days", selected: false },
              { label: "Last 12 Months", selected: false },
              { label: "Enter a Range", selected: false },
            ],
          },
        ]}
      />

      <GetLinksModal
        open={getLinkModalOpen}
        onClose={() => setGetLinkModalOpen(false)}
        links={generatedLinks}
      />

      <BulkAreaModal
        open={bulkAreaModalOpen}
        onClose={() => setBulkAreaModalOpen(false)}
        onUpdate={handleBulkAreaUpdate}
        areas={areas}
        loading={bulkAreaLoading}
      />

      <ConfirmationModal
        open={getLinkTemplateSelectionModalOpen}
        onClose={handleGetLinkTemplateSelectionClose}
        onConfirm={handleGetLinkTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for the narrator page settings."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown
        templates={activeEmailTemplates}
        selectedTemplate={selectedGetLinkTemplate}
        onTemplateChange={(e) => setSelectedGetLinkTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      <ConfirmationModal
        open={templateSelectionModalOpen}
        onClose={handleTemplateSelectionClose}
        onConfirm={handleTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select an email template for these messages."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />
      <ConfirmationModal
        open={emailPromptModalOpen}
        onClose={() => setEmailPromptModalOpen(false)}
        onConfirm={handleEmailPromptContinue}
        title="Narrator Details Required"
        message="Please enter the missing details needed to send the update link."
        textFieldValue={narratorEmail}
        onTextFieldChange={(e) => setNarratorEmail(e.target.value)}
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        narratorName={narratorsWithoutEmails[currentNarratorIndex]?.customNarrator?.name || narratorsWithoutEmails[currentNarratorIndex]?.quicktales[0]?.narrator}
        firstName={narratorFirstName}
        onFirstNameChange={(e) => setNarratorFirstName(e.target.value)}
        lastName={narratorLastName}
        onLastNameChange={(e) => setNarratorLastName(e.target.value)}
      />

      <ConfirmationModal
        open={showMissingNarratorModal}
        onClose={handleMissingNarratorCancel}
        onConfirm={handleMissingNarratorContinue}
        title="Missing Narrator"
        message={
          <span>
            The QuickTale, <i>{missingNarratorQueue[0]?.title}</i>, does not have a narrator (Told By). Skip <i>{missingNarratorQueue[0]?.title}</i> and continue with the other QuickTales or cancel?
          </span>
        }
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default BatchTableContainer;
